/**
 * Cart Drawer Component
 * Handles cart count updates, announcement bar replication, and section refreshing
 */

let noteContainer;
let checkbox;
let content;
let textarea;

function applyCartCount(count) {
  // Select all cart-dot elements (header__cart-dot is the correct class)
  const dots = document.querySelectorAll('cart-dot.header__cart-dot');
  if (!dots.length) return;

  dots.forEach(dot => {
    dot.textContent = count;

    if (count > 0) {
      dot.classList.add('is-visible', 'header-cart__count');
    } else {
      dot.classList.remove('is-visible', 'header-cart__count');
    }
  });

  // Also update cart-count elements (text version in header)
  const cartCountElements = document.querySelectorAll('cart-count');
  cartCountElements.forEach(el => {
    el.textContent = count;
  });
}

function updateCartDotCountFromCart(cart) {
  const count = cart?.item_count ?? 0;
  applyCartCount(count);
}

// Update cart dot count
function updateCartDotCount() {
  fetch('/cart.js', { credentials: 'same-origin' })
    .then(res => res.json())
    .then(cart => {
      updateCartDotCountFromCart(cart);
    })
    .catch(err => console.error('Failed to update cart dot:', err));
}

// Initialize cart note toggle functionality
function initCartNote() {
  noteContainer = document.querySelector(".cart-flyout-note");
  if (!noteContainer) return;

  checkbox = noteContainer.querySelector(".cart-flyout-note-toggle-input");
  content = noteContainer.querySelector(".cart-flyout-note-content");
  textarea = noteContainer.querySelector("textarea");

  updateCartNoteUI(noteContainer, checkbox, content, textarea);

  checkbox.addEventListener("change", () => {
    updateCartNoteUI(noteContainer, checkbox, content, textarea);
  });
}

// Update cart note UI state
function updateCartNoteUI(noteContainer, checkbox, content, textarea) {
  const checked = !!checkbox.checked;
  if (checked) {
    noteContainer.classList.add("is-active");
    content.style.display = "block";
    if (textarea) {
      setTimeout(() => textarea.focus(), 50);
    }
  } else {
    noteContainer.classList.remove("is-active");
    content.style.display = "none";
  }

  checkbox.setAttribute("aria-checked", String(checked));
  const label = noteContainer.querySelector(".cart-flyout-note-toggle-label");
  if (label) {
    label.setAttribute("aria-expanded", String(checked));
  }
}

// Save cart note via AJAX (Shopify Cart API)
let saveNoteTimeout = null;
function saveCartNote(note) {
  // Debounce: wait 500ms after user stops typing
  clearTimeout(saveNoteTimeout);
  saveNoteTimeout = setTimeout(() => {
    const rootUrl = window.Shopify?.routes?.root || '/';

    fetch(rootUrl + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ note: note })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(cart => {
      console.log('Cart note saved successfully:', cart.note);
    })
    .catch(error => {
      console.error('Failed to save cart note:', error);
    });
  }, 500);
}

// Save cart note synchronously (returns a promise)
function saveCartNoteSync(note) {
  const rootUrl = window.Shopify?.routes?.root || '/';

  return fetch(rootUrl + 'cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ note: note })
  })
  .then(response => response.json())
  .then(cart => {
    console.log('Cart note saved:', cart.note);
    return cart;
  });
}

// Initialize cart note saving via delegation (works for drawer and cart page)
function initCartNoteSaving() {
  // Save on input (debounced)
  document.addEventListener('input', function(event) {
    if (event.target.name === 'note' && event.target.tagName === 'TEXTAREA') {
      saveCartNote(event.target.value);
    }
  });

  // Save immediately on blur
  document.addEventListener('blur', function(event) {
    if (event.target.name === 'note' && event.target.tagName === 'TEXTAREA') {
      clearTimeout(saveNoteTimeout);
      saveCartNoteSync(event.target.value);
    }
  }, true);

  // Intercept checkout form submission to ensure note is saved
  document.addEventListener('submit', function(event) {
    const form = event.target;

    // Check if this is a checkout form (cart drawer or cart page)
    if (form.action && form.action.includes('/cart')) {
      // Find any cart note textarea on the page
      const noteTextarea = document.querySelector('textarea[name="note"]');

      if (noteTextarea && noteTextarea.value) {
        // Prevent form submission
        event.preventDefault();

        // Save the note first, then submit the form
        saveCartNoteSync(noteTextarea.value)
          .then(() => {
            // Re-submit the form after note is saved
            form.submit();
          })
          .catch(error => {
            console.error('Failed to save note before checkout:', error);
            // Submit anyway to not block checkout
            form.submit();
          });
      }
    }
  });
}


// Reinitialize Navidium shipping protection widget
function reinitializeNavidium() {
  if (typeof nvd_init === 'function') {
    try {
      nvd_init();
    } catch (e) {
      console.warn('Navidium reinitialization failed:', e);
    }
  }
}

// Section rendering helpers
const getSectionInnerHTML = (html, selector) =>
  new DOMParser()
    .parseFromString(html, "text/html")
    .querySelector(
      selector.slice(0, 1) === "#" && !selector.includes("shopify-section")
        ? `#shopify-section-${selector.slice(1)}`
        : selector
    ).innerHTML;

const renderContents = (parsedState, sectionsToRender) => {
  sectionsToRender.forEach((section) => {
    const sectionElement = section.selector
      ? document.querySelector(section.selector)
      : document.getElementById(section.id);
    if (parsedState[section.id] && sectionElement) {
      sectionElement.innerHTML = getSectionInnerHTML(
        parsedState[section.id],
        section.selector
      );
    }
  });
};

// Fetch and update sections
const getSectionsAndUpdate = async ({
  sectionsLimitReached,
  startRemovePosition = 0,
  sectionsConfig,
}) => {
  const sectionsToFetch = (
    sectionsLimitReached
      ? sectionsConfig.slice(
          startRemovePosition,
          sectionsConfig.length < startRemovePosition + 5
            ? sectionsConfig.length
            : startRemovePosition + 5
        )
      : sectionsConfig
  ).reduce(
    (curr, section, i) => (i ? `${curr},${section.id}` : curr),
    sectionsConfig[startRemovePosition].id
  );

  const sectionData = await fetch(
    `${window.location.pathname}?sections=${sectionsToFetch}`
  );
  const data = await sectionData.json();
  renderContents(data, sectionsConfig);
};

// Section configurations
const getCommonSectionsToRender = () => {
  return [
    {
      id: "cart-dot",
      selector: ".header__cart-dot",
    },
  ];
};

const getCartDrawerSectionsToRender = () => {
  return [
    {
      id: "cart-drawer",
      selector: "cart-drawer",
    },
  ];
};

const getCartPageSectionsToRender = () => {
  // Try to find the main cart section dynamically
  const mainCartSection = document.querySelector('[id^="shopify-section-template"][id*="main"]');
  const mainCartSectionId = mainCartSection ? mainCartSection.id.replace('shopify-section-', '') : null;

  const sections = [
    {
      id: "cart-live-region-text",
      selector: "#cart-live-region-text",
    },
  ];

  if (mainCartSectionId) {
    sections.unshift({
      id: mainCartSectionId,
      selector: `#shopify-section-${mainCartSectionId}`,
    });
  }

  return sections;
};

// Check conditions and update sections
const checkSectionConditionsAndUpdate = () => {
  const isCartPage = window.location.pathname.includes("/cart");

  const sectionsConfig = [
    ...getCommonSectionsToRender(),
    ...(isCartPage
      ? getCartPageSectionsToRender()
      : getCartDrawerSectionsToRender()),
  ];

  const sectionUpdateConfig = {
    sectionsLimitReached: false,
    sectionsConfig,
  };

  if (sectionsConfig.length > 5) {
    let startRemovePosition = 0;

    for (let i = 0; i < Math.ceil(sectionsConfig.length / 5); i += 1) {
      getSectionsAndUpdate({
        ...sectionUpdateConfig,
        startRemovePosition,
        sectionsLimitReached: true,
      });
      startRemovePosition += 5;
    }
  } else {
    getSectionsAndUpdate(sectionUpdateConfig);
  }
};

// Preorder banner - check cart for preorder items and show/hide banner
async function updatePreorderBanner() {
  try {
    const banner = document.querySelector('[data-preorder-banner]');
    if (!banner) return;

    // Check DOM for preorder line items (set by Liquid)
    const hasPreorderItem = !!document.querySelector('[data-preorder-item="true"]');

    // Show/hide banner based on preorder status
    banner.style.display = hasPreorderItem ? '' : 'none';
  } catch (error) {
    console.warn('Failed to update preorder banner:', error);
  }
}

// Initialize on DOM load
document.addEventListener('DOMContentLoaded', () => {
  updateCartDotCount();
  initCartNote();
  initCartNoteSaving();
});

// Handle cart note toggle via delegation
// Listen for clicks on the label text specifically (for Safari compatibility)
document.addEventListener('click', function(event) {
  const labelSpan = event.target.closest('.cart-flyout-note-toggle-label');
  if (!labelSpan) return;

  const container = event.target.closest('.cart-flyout-note');
  if (!container) return;

  const checkbox = container.querySelector('.cart-flyout-note-toggle-input');
  const content = container.querySelector('.cart-flyout-note-content');
  const textarea = container.querySelector('textarea');

  if (!checkbox || !content) return;

  // Prevent the native label behavior and handle manually for consistency
  event.preventDefault();
  event.stopPropagation();

  checkbox.checked = !checkbox.checked;
  updateCartNoteUI(container, checkbox, content, textarea);
});

// Also handle direct checkbox changes (for when checkbox itself is clicked)
document.addEventListener('change', function(event) {
  if (!event.target.classList.contains('cart-flyout-note-toggle-input')) return;

  const container = event.target.closest('.cart-flyout-note');
  if (!container) return;

  const checkbox = event.target;
  const content = container.querySelector('.cart-flyout-note-content');
  const textarea = container.querySelector('textarea');

  if (!content) return;

  updateCartNoteUI(container, checkbox, content, textarea);
});

// Listen for cart:refresh event (triggered by Monk and other apps)
// Note: The built-in CartDrawer component handles the section refresh and fires cart-drawer:refreshed when done
document.addEventListener('cart:refresh', (event) => {
  if (event.detail?.cart) {
    updateCartDotCountFromCart(event.detail.cart);
  } else {
    updateCartDotCount();
  }
});

// Listen for cart:added event (triggered by quick-buy and add to cart actions)
document.addEventListener('cart:added', (event) => {
  if (event.detail?.cart) {
    updateCartDotCountFromCart(event.detail.cart);
  } else {
    updateCartDotCount();
  }
});

// Listen for cart:change event (triggered by Prestige theme's product-form on PDP)
document.documentElement.addEventListener('cart:change', (event) => {
  if (event.detail?.cart) {
    updateCartDotCountFromCart(event.detail.cart);
  } else {
    updateCartDotCount();
  }
});

// Listen for variant:add event (triggered by Prestige theme when variant is added)
document.addEventListener('variant:add', (event) => {
  if (event.detail?.cart) {
    updateCartDotCountFromCart(event.detail.cart);
  } else {
    updateCartDotCount();
  }
}, true); // Use capture to catch it bubbling from form

// Listen for cart-drawer:refreshed event (triggered by Prestige theme's built-in CartDrawer)
document.addEventListener('cart-drawer:refreshed', () => {
  setTimeout(() => {
    initCartNote();
    reinitializeNavidium();
    updatePreorderBanner();
  }, 50);
});

export {
  updateCartDotCount,
  reinitializeNavidium,
  checkSectionConditionsAndUpdate
};
